import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { FaUser, FaMap, FaGlobe, FaHeart, FaCompass, FaBars, FaTimes, FaSignOutAlt, FaCog, FaChartBar } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from '../Auth/AuthModal';
import Settings from '../Settings';

const NAV_ITEMS = [
  { path: '/', icon: <FaGlobe className="w-5 h-5" />, label: 'Home' },
  { path: '/explore', icon: <FaCompass className="w-5 h-5" />, label: 'Explore' },
  { path: '/favorites', icon: <FaHeart className="w-5 h-5" />, label: 'Favorites' },
  { path: '/map', icon: <FaMap className="w-5 h-5" />, label: 'Map' },
  { path: '/analytics', icon: <FaChartBar className="w-5 h-5" />, label: 'Analytics' }
];

const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { mode } = useTheme();
  const { user, logout } = useAuth();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authView, setAuthView] = useState<'login' | 'signup'>('login');
  const [showSettings, setShowSettings] = useState(false);
  const isFairyTheme = mode === 'fairy';
  const isNeonTheme = mode === 'neon';

  const handleAuthClick = (view: 'login' | 'signup') => {
    setAuthView(view);
    setShowAuthModal(true);
    setShowMobileMenu(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowMobileMenu(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSettingsClick = () => {
    setShowSettings(!showSettings);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 h-16 z-50 ${isFairyTheme
      ? 'bg-purple-900/95 border-b border-purple-500/20'
      : isNeonTheme
        ? 'bg-black/95 border-b border-[#00FF00]/20'
        : 'bg-gray-900/95'
      } backdrop-blur-md`}>
      <div className="container mx-auto px-4 h-full">
        <div className="flex items-center justify-between h-full">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/radio.svg" alt="AuraFY" className="w-8 h-8" />
            <span className={`ml-2 text-xl font-bold ${isFairyTheme ? 'magical-text' : isNeonTheme ? 'text-[#00FF00]' : 'text-white'}`}>
              AuraFY
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors
                    ${isActive
                      ? isFairyTheme
                        ? 'bg-purple-800/60 text-purple-100'
                        : isNeonTheme
                          ? 'bg-[#00FF00]/20 text-[#00FF00]'
                          : 'bg-gray-700 text-white'
                      : isFairyTheme
                        ? 'text-purple-200 hover:bg-purple-800/40 hover:text-purple-100'
                        : isNeonTheme
                          ? 'text-[#00FF00]/80 hover:bg-[#00FF00]/10 hover:text-[#00FF00]'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-2">
            {/* Settings Button */}
            <button
              onClick={handleSettingsClick}
              className={`p-2 rounded-lg transition-colors ${isFairyTheme
                ? 'text-purple-200 hover:bg-purple-800/60'
                : isNeonTheme
                  ? 'text-[#00FF00]/80 hover:bg-[#00FF00]/20'
                  : 'text-gray-300 hover:bg-gray-700'}`}
            >
              <FaCog className="w-5 h-5" />
            </button>

            {user ? (
              <Menu as="div" className="relative">
                <Menu.Button className={`p-2 rounded-lg transition-colors ${isFairyTheme
                  ? 'text-purple-200 hover:bg-purple-800/60'
                  : isNeonTheme
                    ? 'text-[#00FF00]/80 hover:bg-[#00FF00]/20'
                    : 'text-gray-300 hover:bg-gray-700'}`}>
                  <FaUser className="w-5 h-5" />
                </Menu.Button>
                <Transition
                  enter="transition duration-100 ease-out"
                  enterFrom="transform scale-95 opacity-0"
                  enterTo="transform scale-100 opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform scale-100 opacity-100"
                  leaveTo="transform scale-95 opacity-0"
                >
                  <Menu.Items className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg py-1 ${isFairyTheme
                    ? 'bg-purple-900/95 border border-purple-500/20'
                    : isNeonTheme
                      ? 'bg-black border border-[#00FF00]/20'
                      : 'bg-gray-800 border border-gray-700'}`}>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/profile"
                          className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 ${active
                            ? isFairyTheme
                              ? 'bg-purple-800/60'
                              : isNeonTheme
                                ? 'bg-[#00FF00]/20'
                                : 'bg-gray-700'
                            : ''} ${isFairyTheme ? 'text-purple-200' : isNeonTheme ? 'text-[#00FF00]' : 'text-white'}`}
                        >
                          <FaUser className="w-5 h-5" />
                          <span>{t('profile.title')}</span>
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 ${active
                            ? isFairyTheme
                              ? 'bg-purple-800/60'
                              : isNeonTheme
                                ? 'bg-[#00FF00]/20'
                                : 'bg-gray-700'
                            : ''} ${isFairyTheme ? 'text-purple-200' : isNeonTheme ? 'text-[#00FF00]' : 'text-white'}`}
                        >
                          <FaSignOutAlt />
                          <span>{t('auth.sign_out')}</span>
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleAuthClick('login')}
                  className={`px-4 py-2 rounded-lg transition-colors ${isFairyTheme
                    ? 'text-purple-200 hover:bg-purple-800/60'
                    : isNeonTheme
                      ? 'text-[#00FF00] hover:bg-[#00FF00]/20'
                      : 'text-gray-300 hover:bg-gray-700'}`}
                >
                  {t('auth.sign_in')}
                </button>
                <button
                  onClick={() => handleAuthClick('signup')}
                  className={`px-4 py-2 rounded-lg transition-colors ${isFairyTheme
                    ? 'bg-purple-500 text-white hover:bg-purple-600'
                    : isNeonTheme
                      ? 'bg-[#00FF00] text-black hover:bg-[#00FF00]/90'
                      : 'bg-primary text-white hover:bg-primary/90'}`}
                >
                  {t('auth.sign_up')}
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className={`md:hidden p-2 rounded-lg transition-colors ${isFairyTheme
              ? 'text-purple-200 hover:bg-purple-800/60'
              : isNeonTheme
                ? 'text-[#00FF00]/80 hover:bg-[#00FF00]/20'
                : 'text-gray-300 hover:bg-gray-700'}`}
          >
            {showMobileMenu ? (
              <FaTimes className="w-6 h-6" />
            ) : (
              <FaBars className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <Transition
        show={showMobileMenu}
        enter="transition-opacity duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className={`md:hidden absolute top-16 left-0 right-0 ${isFairyTheme
          ? 'bg-purple-900/95 border-b border-purple-500/20'
          : isNeonTheme
            ? 'bg-black/95 border-b border-[#00FF00]/20'
            : 'bg-gray-900/95'} backdrop-blur-md`}>
          <div className="container mx-auto px-4 py-4 space-y-2">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors
                    ${isActive
                      ? isFairyTheme
                        ? 'bg-purple-800/60 text-purple-100'
                        : isNeonTheme
                          ? 'bg-[#00FF00]/20 text-[#00FF00]'
                          : 'bg-gray-700 text-white'
                      : isFairyTheme
                        ? 'text-purple-200 hover:bg-purple-800/40'
                        : isNeonTheme
                          ? 'text-[#00FF00]/80 hover:bg-[#00FF00]/10'
                          : 'text-gray-300 hover:bg-gray-700'
                    }`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              );
            })}

            {/* Add Profile Link for logged in users */}
            {user && (
              <Link
                to="/profile"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors
                  ${location.pathname === '/profile'
                    ? isFairyTheme
                      ? 'bg-purple-800/60 text-purple-100'
                      : isNeonTheme
                        ? 'bg-[#00FF00]/20 text-[#00FF00]'
                        : 'bg-gray-700 text-white'
                    : isFairyTheme
                      ? 'text-purple-200 hover:bg-purple-800/40'
                      : isNeonTheme
                        ? 'text-[#00FF00]/80 hover:bg-[#00FF00]/10'
                        : 'text-gray-300 hover:bg-gray-700'
                  }`}
                onClick={() => setShowMobileMenu(false)}
              >
                <FaUser className="w-5 h-5" />
                <span>{t('profile.title')}</span>
              </Link>
            )}

            {/* Mobile Settings Button */}
            <button
              onClick={() => {
                handleSettingsClick();
                setShowMobileMenu(false);
              }}
              className={`w-full flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${isFairyTheme
                ? 'text-purple-200 hover:bg-purple-800/60'
                : isNeonTheme
                  ? 'text-[#00FF00] hover:bg-[#00FF00]/20'
                  : 'text-gray-300 hover:bg-gray-700'}`}
            >
              <FaCog className="w-5 h-5" />
              <span>Settings</span>
            </button>

            {/* Auth Buttons */}
            {user ? (
              <button
                onClick={handleLogout}
                className={`w-full flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${isFairyTheme
                  ? 'text-purple-200 hover:bg-purple-800/60'
                  : isNeonTheme
                    ? 'text-[#00FF00] hover:bg-[#00FF00]/20'
                    : 'text-gray-300 hover:bg-gray-700'}`}
              >
                <FaSignOutAlt className="w-5 h-5" />
                <span>{t('auth.sign_out')}</span>
              </button>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => handleAuthClick('login')}
                  className={`w-full flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${isFairyTheme
                    ? 'text-purple-200 hover:bg-purple-800/60'
                    : isNeonTheme
                      ? 'text-[#00FF00] hover:bg-[#00FF00]/20'
                      : 'text-gray-300 hover:bg-gray-700'}`}
                >
                  <FaUser className="w-5 h-5" />
                  <span>{t('auth.sign_in')}</span>
                </button>
                <button
                  onClick={() => handleAuthClick('signup')}
                  className={`w-full flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${isFairyTheme
                    ? 'bg-purple-500 text-white hover:bg-purple-600'
                    : isNeonTheme
                      ? 'bg-[#00FF00] text-black hover:bg-[#00FF00]/90'
                      : 'bg-primary text-white hover:bg-primary/90'}`}
                >
                  <FaUser className="w-5 h-5" />
                  <span>{t('auth.sign_up')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </Transition>

      {/* Settings Modal */}
      {showSettings && <Settings onClose={() => setShowSettings(false)} />}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialView={authView}
      />
    </header>
  );
};

export default Header; 